/**
 * Copy facts — a campaign's copy guidance: the facts its ad copy may rely on,
 * and the claims no ad may make.
 *
 * The facts go to the AI copywriter and to the critique (which scores
 * compliance against them). The banned claims are a hard rule the SERVER
 * enforces: a generated line containing one is dropped, and a creative with one
 * is refused on save, approve and deploy (422 BANNED_CLAIM).
 *
 * `CopyGuidanceLists` is the two-list editor, shared with the campaign wizard
 * (which pre-fills the Fias defaults for a new campaign). `CopyGuidanceCard`
 * is the campaign page's card. An existing campaign without guidance is never
 * given the defaults silently — the person chooses "Start from the Fias
 * defaults" and saves.
 */

import { useId, useState } from 'react';
import type { AdoptCampaignDto, AdoptCopyGuidanceDto } from '@/lib/api';
import { useUpdateAdoptCampaign } from '../hooks/useAdvertisingOptimization';
import {
  BANNED_CLAIM_MAX_CHARS,
  BANNED_CLAIMS_MAX,
  COPY_FACT_MAX_CHARS,
  COPY_FACTS_MAX,
  DEFAULT_COPY_GUIDANCE,
} from '../copy-guidance';

/** A fresh, editable copy of the platform defaults. */
export function defaultCopyGuidance(): AdoptCopyGuidanceDto {
  return {
    facts: [...DEFAULT_COPY_GUIDANCE.facts],
    bannedClaims: [...DEFAULT_COPY_GUIDANCE.bannedClaims],
  };
}

function ListEditor({
  label,
  hint,
  items,
  onChange,
  maxItems,
  maxChars,
  itemNoun,
  placeholder,
  inline,
}: {
  label: string;
  hint: string;
  items: string[];
  onChange: (next: string[]) => void;
  maxItems: number;
  maxChars: number;
  itemNoun: string;
  placeholder: string;
  /** Render items as compact chips (banned claims) rather than rows (facts). */
  inline?: boolean;
}) {
  const inputId = useId();
  const [draft, setDraft] = useState('');
  const text = draft.trim().replace(/\s+/g, ' ');
  const duplicate = items.some((i) => i.toLowerCase() === text.toLowerCase());
  const full = items.length >= maxItems;
  const canAdd = text.length > 0 && text.length <= maxChars && !duplicate && !full;

  const add = (): void => {
    if (!canAdd) return;
    onChange([...items, text]);
    setDraft('');
  };

  return (
    <div className="flex flex-col gap-2">
      <div>
        <label htmlFor={inputId} className="text-sm font-medium">
          {label}
        </label>
        <p className="text-xs text-muted-foreground">{hint}</p>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">None yet.</p>
      ) : inline ? (
        <ul className="flex flex-wrap gap-1.5" aria-label={`${label} (current)`}>
          {items.map((item, i) => (
            <li
              key={`${item}-${i}`}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/40 pl-2.5 pr-1 py-0.5 text-xs"
            >
              <span>{item}</span>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                aria-label={`Remove ${itemNoun} "${item}"`}
                className="rounded-full px-1 text-muted-foreground hover:text-foreground"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col gap-1" aria-label={`${label} (current)`}>
          {items.map((item, i) => (
            <li
              key={`${item}-${i}`}
              className="flex items-start justify-between gap-2 rounded-md border border-border px-2 py-1.5 text-sm"
            >
              <span>{item}</span>
              <button
                type="button"
                onClick={() => onChange(items.filter((_, j) => j !== i))}
                aria-label={`Remove ${itemNoun} ${i + 1}`}
                className="shrink-0 text-xs text-muted-foreground hover:text-foreground"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-2">
        <input
          id={inputId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          maxLength={maxChars}
          disabled={full}
          placeholder={full ? `At most ${maxItems}` : placeholder}
          className="flex-1 min-w-0 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        />
        <span className="text-xs tabular-nums text-muted-foreground" aria-live="polite">
          {text.length}/{maxChars}
        </span>
        <button
          type="button"
          onClick={add}
          disabled={!canAdd}
          className="px-2.5 py-1.5 rounded-md border border-border text-sm disabled:opacity-50"
        >
          Add {itemNoun}
        </button>
      </div>
      {duplicate && text && <p className="text-xs text-muted-foreground">Already listed.</p>}
      <p className="text-xs text-muted-foreground">
        {items.length}/{maxItems}
      </p>
    </div>
  );
}

/** The two lists: facts (rows) and banned claims (chips). */
export function CopyGuidanceLists({
  value,
  onChange,
}: {
  value: AdoptCopyGuidanceDto;
  onChange: (next: AdoptCopyGuidanceDto) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      <ListEditor
        label="Facts the ads may use"
        hint="True statements about what you promote. The AI writes from these and the reviewer checks the copy against them."
        items={value.facts}
        onChange={(facts) => onChange({ ...value, facts })}
        maxItems={COPY_FACTS_MAX}
        maxChars={COPY_FACT_MAX_CHARS}
        itemNoun="fact"
        placeholder="e.g. Publishing an app requires a free account"
      />
      <ListEditor
        label="Banned claims"
        hint="Words or phrases no ad may contain (any capitalization or punctuation). An ad with one can never be approved or deployed."
        items={value.bannedClaims}
        onChange={(bannedClaims) => onChange({ ...value, bannedClaims })}
        maxItems={BANNED_CLAIMS_MAX}
        maxChars={BANNED_CLAIM_MAX_CHARS}
        itemNoun="banned claim"
        placeholder="e.g. instantly"
        inline
      />
    </div>
  );
}

/** The campaign page's "Copy facts" card. */
export function CopyGuidanceCard({
  holderId,
  campaign,
}: {
  holderId: string;
  campaign: AdoptCampaignDto;
}) {
  const update = useUpdateAdoptCampaign(holderId, campaign.id);
  const [draft, setDraft] = useState<AdoptCopyGuidanceDto | null>(null);
  const guidance = campaign.copyGuidance ?? null;

  const save = (): void => {
    if (!draft) return;
    update.mutate(
      { version: campaign.version, copyGuidance: draft },
      { onSuccess: () => setDraft(null) },
    );
  };

  return (
    <section
      className="rounded-lg border border-border p-4 flex flex-col gap-3"
      aria-label="Copy facts"
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-medium">Copy facts</h2>
          <p className="text-xs text-muted-foreground">
            What the ad copy may say, and what it must never say.
          </p>
        </div>
        {!draft && (
          <div className="flex gap-2">
            {!guidance && (
              <button
                type="button"
                onClick={() => setDraft(defaultCopyGuidance())}
                className="text-sm text-primary hover:underline"
              >
                Start from the Fias defaults
              </button>
            )}
            <button
              type="button"
              onClick={() =>
                setDraft(
                  guidance
                    ? { facts: [...guidance.facts], bannedClaims: [...guidance.bannedClaims] }
                    : { facts: [], bannedClaims: [] },
                )
              }
              className="text-sm text-primary hover:underline"
            >
              {guidance ? 'Edit' : 'Set copy facts'}
            </button>
          </div>
        )}
      </div>

      {draft ? (
        <>
          <CopyGuidanceLists value={draft} onChange={setDraft} />
          {update.isError && (
            <div className="text-sm text-rose-600" role="alert">
              {update.error instanceof Error ? update.error.message : 'Could not save'}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={save}
              disabled={update.isPending}
              className="px-3 py-1.5 rounded-md bg-primary text-primary-foreground text-sm disabled:opacity-50"
            >
              {update.isPending ? 'Saving…' : 'Save copy facts'}
            </button>
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="px-3 py-1.5 rounded-md border border-border text-sm"
            >
              Cancel
            </button>
          </div>
        </>
      ) : guidance && (guidance.facts.length > 0 || guidance.bannedClaims.length > 0) ? (
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <h3 className="text-xs font-medium text-muted-foreground mb-1">Facts</h3>
            {guidance.facts.length === 0 ? (
              <p className="text-sm text-muted-foreground">None.</p>
            ) : (
              <ul className="list-disc pl-4 text-sm flex flex-col gap-0.5">
                {guidance.facts.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <h3 className="text-xs font-medium text-muted-foreground mb-1">Banned claims</h3>
            {guidance.bannedClaims.length === 0 ? (
              <p className="text-sm text-muted-foreground">None.</p>
            ) : (
              <ul className="flex flex-wrap gap-1.5">
                {guidance.bannedClaims.map((c) => (
                  <li key={c} className="rounded-full border border-border px-2 py-0.5 text-xs">
                    {c}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">
          No copy facts yet. Without them the AI only knows the listing text, and nothing stops a
          claim you would not make.
        </p>
      )}
    </section>
  );
}
